import { useTranslation } from 'react-i18next';

import type { CustomCondition } from '~/api/conditions/types';

import { usePreviewCustomCondition } from '~/api/conditions';
import { AlertDialog, Button, Dialog, Menu, PdfPreview, Tooltip } from '~/components';
import { toast } from '~/utils/toast';

import type { DialogHandles } from '../CustomConditionsTable.hooks';

import { previewBodyStyle } from './customConditionCell.css';

type Props = {
  customCondition: CustomCondition;
  dialogHandles: DialogHandles;
};

export const CustomConditionOptionsCell = ({ customCondition, dialogHandles }: Props) => {
  const previewMutation = usePreviewCustomCondition(customCondition.id);

  const previewHandle = Dialog.useHandle<string>();

  const { t } = useTranslation(['common', 'settings']);

  return (
    <div>
      <Menu.Root>
        <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} icon="MoreVert" loading={previewMutation.isPending} />} />

        <Menu.List>
          <Tooltip content={t('settings:general.customConditions.table.actions.creatingPdf')} disabled={customCondition.pdfCreated}>
            <Menu.Item
              disabled={!customCondition.pdfCreated}
              icon="Visibility"
              onClick={() =>
                previewMutation.mutate(undefined, {
                  onSuccess: (pdf) => previewHandle.openWithPayload(pdf),
                  onError: () => toast.danger({ description: t('common:error') }),
                })
              }
            >
              {t('settings:general.customConditions.table.actions.view')}
            </Menu.Item>
          </Tooltip>

          <AlertDialog.Trigger
            handle={dialogHandles.deleteCustomConditionHandle}
            nativeButton={false}
            payload={customCondition}
            render={<Menu.Item icon="Trash">{t('settings:general.customConditions.table.actions.delete')}</Menu.Item>}
          />
        </Menu.List>
      </Menu.Root>

      <Dialog.Root handle={previewHandle}>
        {({ payload: pdf }) =>
          pdf && (
            <Dialog.Body>
              <Dialog.Header title={customCondition.name} />

              <div className={previewBodyStyle}>
                <PdfPreview src={`data:application/pdf;base64,${pdf}`} title={customCondition.name} />
              </div>
            </Dialog.Body>
          )
        }
      </Dialog.Root>
    </div>
  );
};
